import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  Chip,
  Alert,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SendIcon from "@mui/icons-material/Send";
import { submit_custom_land_enquiry, get_my_custom_land_enquiries } from "../../../api/ControllApi";

export default function CustomLandEnquiry() {
  const [activeTab, setActiveTab] = useState(0); // 0 = Submit, 1 = List
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Form Fields
  const [formData, setFormData] = useState({
    customer_name: "",
    mobile_number: "",
    email_address: "",
    type_of_land: "",
    required_sqft: "",
    purpose: "",
    preferred_location: "",
    budget_range: "",
    additional_requirements: "",
    contact_through: "Phone Call",
  });

  const [formErrors, setFormErrors] = useState({});

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const res = await get_my_custom_land_enquiries();
      if (res && res.data) {
        setEnquiries(res.data);
      }
    } catch (err) {
      console.error("Error fetching enquiries:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchEnquiries();
    }
  }, [activeTab]);

  const validateForm = () => {
    const errors = {};
    if (!formData.customer_name.trim()) errors.customer_name = "Customer name is mandatory.";
    
    if (!formData.mobile_number.trim()) {
      errors.mobile_number = "Mobile number is mandatory.";
    } else if (!/^[0-9]{10}$/.test(formData.mobile_number.trim())) {
      errors.mobile_number = "Mobile number must contain exactly 10 digits.";
    }

    if (formData.email_address.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address.trim())) {
      errors.email_address = "Please enter a valid email address.";
    }

    if (!formData.type_of_land.trim()) errors.type_of_land = "Type of land is mandatory.";
    
    if (!formData.required_sqft.toString().trim()) {
      errors.required_sqft = "Required Sq.ft. size is mandatory.";
    } else {
      const sqft = parseFloat(formData.required_sqft);
      if (isNaN(sqft) || sqft <= 0) {
        errors.required_sqft = "Sq.ft. size must be a number greater than zero.";
      }
    }

    if (!formData.purpose.trim()) errors.purpose = "Purpose of land purchase is mandatory.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setErrorMsg(null);

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const res = await submit_custom_land_enquiry(formData);
      if (res && res.status === 200) {
        setMessage(res.message || "Your enquiry has been submitted successfully. Our team will contact you shortly.");
        // Reset form
        setFormData({
          customer_name: "",
          mobile_number: "",
          email_address: "",
          type_of_land: "",
          required_sqft: "",
          purpose: "",
          preferred_location: "",
          budget_range: "",
          additional_requirements: "",
        });
        setFormErrors({});
      } else {
        setErrorMsg(res.message || "Something went wrong. Please check your inputs.");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.message || "Failed to submit enquiry. Please verify network connection.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    const s = status ? status.toLowerCase() : "new";
    switch (s) {
      case "new":
        return <Chip label="New" size="small" sx={{ bgcolor: "#fff3cd", color: "#856404", fontWeight: 600 }} />;
      case "in progress":
        return <Chip label="In Progress" size="small" sx={{ bgcolor: "#d1ecf1", color: "#0c5460", fontWeight: 600 }} />;
      case "contacted":
        return <Chip label="Contacted" size="small" sx={{ bgcolor: "#cce5ff", color: "#004085", fontWeight: 600 }} />;
      case "site visit scheduled":
        return <Chip label="Site Visit Scheduled" size="small" sx={{ bgcolor: "#d4edda", color: "#155724", fontWeight: 600 }} />;
      case "quotation sent":
        return <Chip label="Quotation Sent" size="small" sx={{ bgcolor: "#d4edda", color: "#155724", fontWeight: 600 }} />;
      case "closed":
        return <Chip label="Closed" size="small" sx={{ bgcolor: "#e2e3e5", color: "#383d41", fontWeight: 600 }} />;
      case "cancelled":
        return <Chip label="Cancelled" size="small" sx={{ bgcolor: "#f8d7da", color: "#721c24", fontWeight: 600 }} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 640,
        mx: "auto",
        bgcolor: "#ffffff",
        borderRadius: "24px",
        border: "1px solid #E6EFE3",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
        p: { xs: 3, md: 4 },
        my: 2,
      }}
    >
      {/* Top Banner Notice */}
      <Box
        sx={{
          bgcolor: "#FAF6E9",
          border: "1px solid #F3EBD4",
          borderRadius: "16px",
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: "#3B6019",
            fontSize: "17px",
            mb: 1,
          }}
        >
          Submit Custom Land Requirements
        </Typography>
        <Typography sx={{ color: "#666666", fontSize: "13px", lineHeight: 1.5 }}>
          Looking for specific land? Submit your custom requirements below, and our team will get back to you with matches.
        </Typography>
      </Box>

      {/* Pill Navigation Tabs */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Button
          onClick={() => setActiveTab(0)}
          sx={{
            flex: 1,
            borderRadius: "24px",
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            bgcolor: activeTab === 0 ? "#5B9E00" : "#ffffff",
            color: activeTab === 0 ? "#ffffff" : "#444444",
            border: activeTab === 0 ? "none" : "1px solid #D8D8D8",
            boxShadow: "none",
            "&:hover": {
              bgcolor: activeTab === 0 ? "#4e8a00" : "#f5f5f5",
            },
          }}
        >
          Submit Land Enquiry
        </Button>
        <Button
          onClick={() => setActiveTab(1)}
          sx={{
            flex: 1,
            borderRadius: "24px",
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            bgcolor: activeTab === 1 ? "#5B9E00" : "#ffffff",
            color: activeTab === 1 ? "#ffffff" : "#444444",
            border: activeTab === 1 ? "none" : "1px solid #D8D8D8",
            boxShadow: "none",
            "&:hover": {
              bgcolor: activeTab === 1 ? "#4e8a00" : "#f5f5f5",
            },
          }}
        >
          My Enquiries List
        </Button>
      </Box>

      {/* Tab Content 1: Submit Form */}
      {activeTab === 0 && (
        <form onSubmit={handleSubmit}>
          {message && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: "12px" }} onClose={() => setMessage(null)}>
              {message}
            </Alert>
          )}
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }} onClose={() => setErrorMsg(null)}>
              {errorMsg}
            </Alert>
          )}

          {/* Customer Information */}
          <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#2B4A10", mb: 1.5 }}>
            Customer Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Customer Name *"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleInputChange}
                error={!!formErrors.customer_name}
                helperText={formErrors.customer_name}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Mobile Number (10 digits) *"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                error={!!formErrors.mobile_number}
                helperText={formErrors.mobile_number}
                inputProps={{ maxLength: 10 }}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Email Address (Optional)"
                name="email_address"
                type="email"
                value={formData.email_address}
                onChange={handleInputChange}
                error={!!formErrors.email_address}
                helperText={formErrors.email_address}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              />
            </Grid>
          </Grid>

          {/* Land Requirement details */}
          <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#2B4A10", mb: 1.5 }}>
            Land Requirement Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="What type of land are you looking for? *"
                name="type_of_land"
                value={formData.type_of_land}
                onChange={handleInputChange}
                error={!!formErrors.type_of_land}
                helperText={formErrors.type_of_land}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="How many Sq.ft. do you require? *"
                name="required_sqft"
                type="number"
                value={formData.required_sqft}
                onChange={handleInputChange}
                error={!!formErrors.required_sqft}
                helperText={formErrors.required_sqft}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="For what purpose do you need this land? *"
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                error={!!formErrors.purpose}
                helperText={formErrors.purpose}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Preferred Location (Optional)"
                name="preferred_location"
                value={formData.preferred_location}
                onChange={handleInputChange}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Budget Range (Optional)"
                name="budget_range"
                value={formData.budget_range}
                onChange={handleInputChange}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                SelectProps={{ native: true }}
                name="contact_through"
                value={formData.contact_through || "Phone Call"}
                onChange={handleInputChange}
                InputProps={{
                  sx: { borderRadius: "24px", height: 46, fontSize: "13px" }
                }}
              >
                <option value="Phone Call">Phone Call</option>
                <option value="WhatsApp">WhatsApp</option>
                <option value="Email">Email</option>
                <option value="WhatsApp / Call">WhatsApp / Call</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Additional Requirements"
                name="additional_requirements"
                value={formData.additional_requirements}
                onChange={handleInputChange}
                InputProps={{
                  sx: { borderRadius: "16px", fontSize: "13px", p: 1.5 }
                }}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            disabled={submitting}
            sx={{
              width: "100%",
              height: 48,
              borderRadius: "24px",
              bgcolor: "#2E5B10",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: "15px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#22460B",
              },
            }}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : "Submit Enquiry"}
          </Button>
        </form>
      )}

      {/* Tab Content 2: List View */}
      {activeTab === 1 && (
        <TableContainer component={Paper} sx={{ borderRadius: 3, border: "1px solid #eef", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#82A240" }} />
            </Box>
          ) : enquiries.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <AssignmentIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Enquiries Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You haven't submitted any custom land requirement enquiries yet.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Enquiry ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Submitted Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type of Land</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Required Size</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Preferred Location</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Budget Range</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enquiries.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell fontWeight={700} color="#82A240">{item.enquiry_id}</TableCell>
                    <TableCell>{new Date(item.created_at).toLocaleString()}</TableCell>
                    <TableCell><Chip label={item.type_of_land} size="small" variant="outlined" /></TableCell>
                    <TableCell>{Number(item.required_sqft).toLocaleString()} Sq.ft.</TableCell>
                    <TableCell>{item.purpose}</TableCell>
                    <TableCell>{item.preferred_location || "-"}</TableCell>
                    <TableCell>{item.budget_range || "-"}</TableCell>
                    <TableCell>{getStatusChip(item.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}
    </Box>
  );
}
